
import React from 'react';
import { Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { WaypointContent } from '../../types';
import { WAYPOINT_ICONS, waypointHasLayerCategory } from '../../services/waypointService';
import { Trash2, Share2 } from 'lucide-react';
import { shareWaypoint } from '../../services/waypointService';
import { renderToStaticMarkup } from 'react-dom/server';
import { useTranslation } from 'react-i18next';

interface UserContentLayerProps {
  waypoints: WaypointContent[];
  onDeleteWaypoint?: (id: string) => void;
  onSelectWaypoint?: (wp: WaypointContent) => void;
  /**
   * When true, render every provided waypoint as-is (skip the linked-spot /
   * visit / layer-category filtering). Used by the "show folder on map" focus
   * overlay so saved points always appear, even when their linked spot isn't
   * loaded in the current viewport.
   */
  bypassFilter?: boolean;
}

function createWaypointIcon(emoji: string, color: string) {
  const html = renderToStaticMarkup(
    <div style={{
      width: 34, height: 34, borderRadius: 12,
      backgroundColor: color + '30',
      border: `2px solid ${color}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 16, lineHeight: 1,
    }}>
      {emoji}
    </div>
  );
  return L.divIcon({
    html,
    className: '',
    iconSize: [34, 34],
    iconAnchor: [17, 17],
  });
}

function createPhotoWaypointIcon(photoUrl: string, color: string) {
  const html = renderToStaticMarkup(
    <div style={{
      width: 38, height: 38, borderRadius: 19,
      border: `3px solid ${color}`,
      overflow: 'hidden',
      boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
    }}>
      <img
        src={photoUrl}
        alt=""
        style={{
          width: '100%', height: '100%',
          objectFit: 'cover',
        }}
      />
    </div>
  );
  return L.divIcon({
    html,
    className: '',
    iconSize: [38, 38],
    iconAnchor: [19, 19],
  });
}

const UserContentLayer: React.FC<UserContentLayerProps> = ({
  waypoints, onDeleteWaypoint, onSelectWaypoint, bypassFilter = false,
}) => {
  const { t } = useTranslation();
  const canDelete = (wp: WaypointContent) =>
    !!onDeleteWaypoint && !wp.id.startsWith('visit_');
  // Skip waypoints som rendres på et annet vis:
  //  - linkedSpotId: tilsvarende delt CampingSpot vises av ClusteredSpotMarkers
  //  - visit_*: håndteres av App.tsx syntheticLayerSpots (alltid synthesisert
  //    som proper layer-spot, eller skippet hvis underliggende spot er lastet)
  //  - waypointHasLayerCategory: manuelle registreringer med kategori
  //    synthetiseres også som CampingSpot
  // Det som blir igjen: brukerens generiske personlige punkter (pin/star uten
  // kategori-match) som beholder den enkle waypoint-popupen.
  return (
    <>
      {waypoints.filter(wp =>
        bypassFilter || (
          !wp.linkedSpotId &&
          !wp.id.startsWith('visit_') &&
          !waypointHasLayerCategory(wp)
        )
      ).map(wp => {
        const meta = WAYPOINT_ICONS.find(w => w.id === wp.icon) ?? WAYPOINT_ICONS[0];
        const hasPhoto = !!wp.photoUrl;
        const icon = hasPhoto
          ? createPhotoWaypointIcon(wp.photoUrl!, wp.color)
          : createWaypointIcon(meta.emoji, wp.color);

        return (
          <Marker
            key={wp.id}
            position={[wp.latitude, wp.longitude]}
            icon={icon}
            eventHandlers={{
              click: () => onSelectWaypoint?.(wp),
            }}
          >
            <Popup>
              <div className="min-w-[140px]">
                {hasPhoto && (
                  <div className="w-full h-24 rounded-lg overflow-hidden mb-1.5 -mt-1">
                    <img src={wp.photoUrl} alt="" className="w-full h-full object-cover" loading="lazy" />
                  </div>
                )}
                <p className="text-[12px] font-bold text-stone-800">{wp.name}</p>
                {wp.note && <p className="text-2xs text-[var(--text-muted)] mt-0.5">{wp.note}</p>}
                <div className="flex items-center gap-1.5 mt-2">
                  <button
                    onClick={(e) => { e.stopPropagation(); shareWaypoint(wp.id, wp.name, wp.latitude, wp.longitude); }}
                    className="flex items-center gap-1 px-2 py-1 bg-blue-50 rounded-lg text-2xs font-bold text-blue-500 hover:bg-blue-100"
                  >
                    <Share2 size={10} /> {t("auto.map.usercontentlayer.del", "Del")}
                  </button>
                  {canDelete(wp) && (
                    <button
                      onClick={(e) => { e.stopPropagation(); onDeleteWaypoint!(wp.id); }}
                      className="flex items-center gap-1 px-2 py-1 bg-red-50 rounded-lg text-2xs font-bold text-red-500 hover:bg-red-100"
                    >
                      <Trash2 size={10} /> {t("auto.map.usercontentlayer.slett", "Slett")}
                    </button>
                  )}
                </div>
              </div>
            </Popup>
          </Marker>
        );
      })}
    </>
  );
};

export default UserContentLayer;
